/**
 * Lock Validation Utilities
 * Phase 2: Validate temporal locks and split dwell by lock boundaries
 */

import { calculateTotalSlots, calculateWordCount, intersects } from "./bitmap";
import { Bucket, ParkingRequest, Spot, TemporalLock } from "./types";

/**
 * Validation result for a single lock
 */
export type LockValidationResult = {
  valid: boolean;
  reason?: string; // Present if invalid
};

/**
 * Validate that a lock can be satisfied
 *
 * Checks:
 * 1. Spot exists
 * 2. Spot allows aircraft size
 * 3. Spot is available during lock period
 *
 * @param lock - Lock to validate
 * @param request - Aircraft making the request
 * @param spots - All available spots
 * @param horizonHours - Planning horizon
 * @returns Validation result with reason if invalid
 */
export function validateLock(
  lock: TemporalLock,
  request: ParkingRequest,
  spots: Spot[],
  horizonHours: number
): LockValidationResult {
  // Check 1: Spot exists
  const spot = spots.find((s) => s.id === lock.spotId);
  if (!spot) {
    return {
      valid: false,
      reason: `Lock spot ${lock.spotId} not found`,
    };
  }

  // Check 2: Size compatibility
  if (!spot.allowedSizes.includes(request.sizeCategory)) {
    return {
      valid: false,
      reason: `Lock spot ${lock.spotId} does not allow ${request.sizeCategory} aircraft`,
    };
  }

  // Check 3: Time availability
  // Create mask for lock period and check against spot occupancy
  const totalSlots = calculateTotalSlots(horizonHours);
  const WORD_COUNT = calculateWordCount(totalSlots);
  const lockMask = new BigUint64Array(WORD_COUNT);

  // Set bits for lock period
  for (let bucket = lock.startBucket; bucket < lock.endBucket; bucket++) {
    const wordIdx = Math.floor(bucket / 64);
    const bitIdx = BigInt(bucket % 64);
    if (wordIdx < WORD_COUNT) {
      lockMask[wordIdx] |= 1n << bitIdx;
    }
  }

  if (intersects(spot.occupancy, lockMask)) {
    return {
      valid: false,
      reason: `Lock spot ${lock.spotId} is occupied during lock period [${lock.startBucket}, ${lock.endBucket})`,
    };
  }

  return { valid: true };
}

/**
 * Time segment with optional lock constraint
 */
export type TimeSegment = {
  start: Bucket;
  end: Bucket;
  lockedSpotId?: string;
};

/**
 * Split aircraft dwell into segments based on lock boundaries
 *
 * Example:
 * - Aircraft: arrival=32, departure=72
 * - Lock: spotId="A3", start=48, end=56
 * - Result: [
 *     { start: 32, end: 48, lockedSpotId: undefined },
 *     { start: 48, end: 56, lockedSpotId: "A3" },
 *     { start: 56, end: 72, lockedSpotId: undefined }
 *   ]
 *
 * @param request - Parking request with optional locks
 * @param bufferMinutes - Buffer to add before/after dwell
 * @returns Array of time segments (locked and non-locked)
 */
export function splitDwellByLocks(
  request: ParkingRequest,
  bufferMinutes: number
): TimeSegment[] {
  const bufferBuckets = Math.ceil(bufferMinutes / 15);
  const dwellStart = Math.max(0, request.arrivalBucket - bufferBuckets);
  const dwellEnd = request.departureBucket + bufferBuckets;

  // No locks → single segment covering full dwell
  if (!request.locks || request.locks.length === 0) {
    return [{ start: dwellStart, end: dwellEnd }];
  }

  // Sort locks chronologically
  const sortedLocks = [...request.locks].sort(
    (a, b) => a.startBucket - b.startBucket
  );

  const segments: TimeSegment[] = [];
  let cursor = dwellStart;

  for (const lock of sortedLocks) {
    // Add non-locked segment before this lock (if gap exists)
    if (cursor < lock.startBucket) {
      segments.push({
        start: cursor,
        end: lock.startBucket,
        lockedSpotId: undefined,
      });
    }

    // Add locked segment
    segments.push({
      start: lock.startBucket,
      end: lock.endBucket,
      lockedSpotId: lock.spotId,
    });

    cursor = lock.endBucket;
  }

  // Add final non-locked segment after last lock (if remaining time)
  if (cursor < dwellEnd) {
    segments.push({
      start: cursor,
      end: dwellEnd,
      lockedSpotId: undefined,
    });
  }

  return segments;
}
